import { ArrowRight, TrendingUp, Sparkles, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-36 pb-24 sm:pt-44 sm:pb-32 overflow-hidden"
    >
      {/* Background glow */}
      <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] bg-[#FF5722]/20 blur-[140px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FF5722]/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Left: Text */}
          <div className="fade-up">
            <div
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#FF5722]/30 bg-[#FF5722]/5 text-[#FF7043] text-xs font-mono tracking-wider uppercase mb-7"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Performance Marketing Agency
            </div>

            <h1
              data-testid="hero-headline"
              className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter leading-[1.02] text-white"
            >
              Scale Your <br />
              Business with <br />
              <span className="gradient-text">High-Performance</span> Ads
            </h1>

            <p
              data-testid="hero-subtext"
              className="mt-7 text-lg sm:text-xl text-zinc-400 max-w-xl leading-relaxed"
            >
              We help brands grow using Facebook Ads and Google Ads through
              data-driven strategies that maximize ROI.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Button
                data-testid="hero-primary-cta"
                size="lg"
                className="bg-[#FF5722] hover:bg-[#FF7043] text-black font-semibold rounded-xl px-7 py-6 text-base shadow-[0_0_40px_-8px_rgba(255,87,34,0.6)] group"
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              >
                Start Your Campaign
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                data-testid="hero-secondary-cta"
                size="lg"
                variant="outline"
                className="rounded-xl px-7 py-6 text-base border-white/15 bg-white/5 hover:bg-white/10 text-white"
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              >
                Book Free Strategy Call
              </Button>
            </div>

            <div className="mt-12 flex items-center gap-6">
              <div className="flex -space-x-3">
                {["#FF5722", "#FFAB40", "#FF7043", "#F4511E"].map((c, i) => (
                  <div
                    key={i}
                    className="w-9 h-9 rounded-full border-2 border-black"
                    style={{ background: `linear-gradient(135deg, ${c}, #1a1a1a)` }}
                  />
                ))}
              </div>
              <div className="text-sm text-zinc-400">
                <span className="text-white font-semibold">50+ Brands</span> scaling with us
              </div>
            </div>
          </div>

          {/* Right: Dashboard Card */}
          <div className="relative fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="absolute -inset-2 bg-[#FF5722]/20 blur-3xl rounded-3xl pointer-events-none" />
            <DashboardCard />
          </div>
        </div>
      </div>
    </section>
  );
}

function DashboardCard() {
  return (
    <div
      data-testid="hero-dashboard-card"
      className="relative glass rounded-3xl p-6 sm:p-7 shadow-2xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-mono tracking-wider text-zinc-500 uppercase">
            Campaign Dashboard
          </div>
          <div className="text-base font-semibold text-white mt-0.5">
            Live Performance
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF5722]/10 border border-[#FF5722]/20">
          <div className="w-2 h-2 rounded-full bg-[#FF5722] pulse-dot" />
          <span className="text-xs text-[#FF7043] font-mono">LIVE</span>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Metric label="ROAS" value="5.2x" change="+23%" />
        <Metric label="Conversions" value="1,847" change="+18%" />
        <Metric label="Revenue" value="$94K" change="+31%" />
      </div>

      {/* Chart */}
      <div className="rounded-2xl bg-black/40 border border-white/5 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#FF5722]" />
            <span className="text-sm text-zinc-300 font-medium">Last 7 days</span>
          </div>
          <span className="text-xs text-zinc-500 font-mono">ROAS Trend</span>
        </div>
        <div className="flex items-end gap-2 h-32">
          {[40, 65, 50, 80, 70, 95, 100].map((h, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
              <div
                className="w-full rounded-t-md origin-bottom"
                style={{
                  height: `${h}%`,
                  background: i === 6
                    ? "linear-gradient(180deg, #FF5722 0%, #FF7043 100%)"
                    : "rgba(255, 87, 34, 0.25)",
                  animation: `bar-grow 0.8s ease-out ${i * 0.07}s both`,
                  transformOrigin: "bottom",
                }}
              />
              <span className="text-[10px] text-zinc-600 font-mono">
                {["M","T","W","T","F","S","S"][i]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-5 flex items-center justify-between pt-4 border-t border-white/5">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#FF5722]" />
          <span className="text-sm text-zinc-300">Outperforming target</span>
        </div>
        <span className="text-sm font-semibold text-[#FF7043]">+42.6%</span>
      </div>
    </div>
  );
}

function Metric({ label, value, change }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4">
      <div className="text-[11px] text-zinc-500 font-mono uppercase tracking-wider">
        {label}
      </div>
      <div className="text-2xl font-bold text-white mt-1.5">{value}</div>
      <div className="text-xs text-[#FF7043] mt-1 font-medium">{change}</div>
    </div>
  );
}
